import React from 'react';
import { useSearchParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import Map, { Marker, NavigationControl, MapRef } from 'react-map-gl/mapbox-legacy';
import { MapPin, Wifi, WifiOff, Search, Layers, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { usePreferences } from '@/hooks/use-preferences';
import { MAPBOX_TOKEN, mapboxForwardGeocode } from '@/lib/mapbox';
import { AssetDetailPanel } from './asset-detail-panel';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Position {
  id: string;
  fleetNumber: string | null;
  registrationNumber: string | null;
  lat: number;
  lon: number;
  online: boolean;
  iot: boolean;
  vehicleStatus?: string | null;
  lastSeen?: string | null;
}

// Auckland, NZ — derived from sample asset coords clustered around -36.7 / 174.7.
// Used only as a hard-coded fallback when no Tracker Settings default address
// is configured (or geocoding fails). The user-set default takes precedence.
const DEFAULT_VIEW = { latitude: -36.85, longitude: 174.76, zoom: 9 };
const ZOOM_WHEN_GEOCODED = 11;

const STYLES = [
  { value: 'mapbox://styles/mapbox/light-v11',             label: 'Light' },
  { value: 'mapbox://styles/mapbox/streets-v12',           label: 'Streets' },
  { value: 'mapbox://styles/mapbox/satellite-streets-v12', label: 'Satellite' },
];

export function AssetTrackerPage() {
  // Drill-down params from the dashboard Tracking card: ?online=true|false
  // seeds the online/offline filter, ?location=<id> scopes to a service
  // location (forwarded to the positions endpoint).
  const [params] = useSearchParams();
  const location = params.get('location') ?? '';
  const onlineParam = params.get('online');
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<'all' | 'online' | 'offline'>(
    onlineParam === 'true' ? 'online' : onlineParam === 'false' ? 'offline' : 'all',
  );
  // Resolve the location name for the scope banner (only when scoped).
  const { data: locations = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['/master-data/service-locations'],
    queryFn: async () => (await api<Array<{ id: string; name: string }>>('/master-data/service-locations?limit=500')).data || [],
    staleTime: 5 * 60_000,
    enabled: !!location,
  });
  const locName = locations.find((l) => l.id === location)?.name;
  // Map style is per-user durable: stored under /auth/me/preferences and
  // restored on every login. Initial value falls back to the first STYLES
  // option until the GET resolves (`ready` flips true).
  const { value: mapStyle, setValue: setMapStyle } =
    usePreferences<string>('asset-tracker:mapStyle', STYLES[0].value);
  const [selected, setSelected] = React.useState<Position | null>(null);
  const mapRef = React.useRef<MapRef | null>(null);

  // Tracker Settings — admin-managed singleton (see /admin/settings).
  // Drives two things on this page:
  //   1. `primaryColour` — brand accent applied inline to the title icon
  //      and the selected-marker ring (the rest of the page sticks with
  //      the app's design-system primary so we don't fight Tailwind).
  //   2. `defaultMapLocation` — geocoded once into the map's initial view.
  const { data: tracker } = useQuery<any>({
    queryKey: ['/tracker-settings'],
    queryFn: async () => (await api<any[]>('/tracker-settings?limit=1')).data?.[0] || null,
    staleTime: 5 * 60_000,
  });
  const primaryColour: string | null = tracker?.primaryColour || null;
  const defaultAddressQuery = React.useMemo(() => {
    const a = tracker?.defaultMapLocation;
    if (!a) return '';
    return [a.addressLine1, a.city].filter(Boolean).join(', ').trim();
  }, [tracker]);

  // Geocode the saved address via the public Mapbox Geocoding API. Cached
  // for an hour — the address rarely changes, and this avoids a round-trip
  // every time the page mounts. Returns null on failure / empty input;
  // we then fall back to DEFAULT_VIEW (Auckland).
  const { data: defaultCoords } = useQuery<{ lat: number; lon: number } | null>({
    queryKey: ['mapbox-geocode', defaultAddressQuery],
    queryFn: async () => {
      if (!defaultAddressQuery) return null;
      const features = await mapboxForwardGeocode(defaultAddressQuery, { limit: 1 });
      const feat = features[0];
      if (!feat || !Array.isArray(feat.center)) return null;
      const [lon, lat] = feat.center;
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return { lat, lon };
    },
    enabled: !!defaultAddressQuery,
    staleTime: 60 * 60_000,
  });

  // When the geocoded default resolves after the map has already mounted
  // (the common case — the query is async), recenter via flyTo. On the
  // very first render, `initialViewState` below picks up the resolved
  // coords if they're already cached.
  React.useEffect(() => {
    if (!defaultCoords) return;
    const m = mapRef.current;
    if (!m) return;
    m.flyTo({ center: [defaultCoords.lon, defaultCoords.lat], zoom: ZOOM_WHEN_GEOCODED, duration: 600 });
  }, [defaultCoords]);

  const initialView = defaultCoords
    ? { latitude: defaultCoords.lat, longitude: defaultCoords.lon, zoom: ZOOM_WHEN_GEOCODED }
    : DEFAULT_VIEW;

  // Fetch fresh on every page load (ignoring the app-wide 30s cache), then
  // every 2 minutes while the page stays open.
  const { data: positions = [], isLoading } = useQuery<Position[]>({
    queryKey: ['/fleet/assets/positions', location],
    queryFn: async () => (await api<Position[]>(`/fleet/assets/positions${location ? `?location=${encodeURIComponent(location)}` : ''}`)).data || [],
    refetchOnMount: 'always',
    refetchInterval: 2 * 60_000,
  });

  // Skip rows with bad coordinates — Mapbox throws "Invalid LngLat" if lat is outside
  // [-90, 90] or lon is outside [-180, 180]. Some legacy Zoho rows have these swapped.
  const validPositions = positions.filter((p) =>
    Number.isFinite(p.lat) && Number.isFinite(p.lon)
    && p.lat >= -90 && p.lat <= 90
    && p.lon >= -180 && p.lon <= 180,
  );

  const filtered = validPositions.filter((p) => {
    if (filter === 'online' && !p.online) return false;
    if (filter === 'offline' && p.online) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.fleetNumber ?? '').toLowerCase().includes(q)
          || (p.registrationNumber ?? '').toLowerCase().includes(q);
    }
    return true;
  });

  const onlineCount  = validPositions.filter((p) => p.online).length;
  const offlineCount = validPositions.length - onlineCount;
  const skippedCount = positions.length - validPositions.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-semibold">Asset Tracker</h1>
          {/* Verbose subtitle on desktop; compact count on mobile/tablet so the
              hero row fits on one line under ~400px wide. */}
          <p className="hidden md:block text-sm text-muted-foreground">
            Live positions for {positions.length} tracked asset(s) — refreshes every 30 seconds.
          </p>
          <p className="md:hidden text-xs text-muted-foreground">
            {positions.length} tracked · refreshes every 30s
          </p>
          {location && (
            <p className="text-xs text-muted-foreground mt-1">
              Filtered to <strong className="text-foreground">{locName ?? 'selected location'}</strong>
              {' · '}
              <Link to="/vehicles/tracker" className="underline hover:text-foreground">show all locations</Link>
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="success" className="gap-1"><Wifi className="h-3 w-3" /> {onlineCount}</Badge>
          <Badge variant="secondary" className="gap-1"><WifiOff className="h-3 w-3" /> {offlineCount}</Badge>
          {skippedCount > 0 && (
            <Badge variant="destructive" title="Records with invalid lat/lon — fix in /fleet/assets">
              {skippedCount} bad
            </Badge>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="pt-3 pb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] md:max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Fleet # or Rego…"
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-1">
            {(['all', 'online', 'offline'] as const).map((f) => (
              <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'online' ? 'Online' : 'Offline'}
              </Button>
            ))}
          </div>
          <div className="hidden md:block flex-1" />
          {/* Map style picker — desktop shows a full select with a "Reset
              to default" affordance. Mobile collapses to an icon-only
              Layers dropdown so it doesn't crowd the filter row. */}
          <Select value={mapStyle} onValueChange={setMapStyle}>
            <SelectTrigger className="hidden md:flex w-[160px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STYLES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {mapStyle !== STYLES[0].value && (
            <Button size="sm" variant="ghost" className="hidden md:inline-flex h-9 text-xs" onClick={() => setMapStyle(STYLES[0].value)}>
              Reset to default
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="md:hidden h-8 w-8 p-0 ml-auto" aria-label="Map style">
                <Layers className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {STYLES.map((s) => (
                <DropdownMenuItem
                  key={s.value}
                  onClick={() => setMapStyle(s.value)}
                  className="justify-between"
                >
                  {s.label}
                  {mapStyle === s.value && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="pb-2 hidden md:block">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin
              className={primaryColour ? 'h-4 w-4' : 'h-4 w-4 text-primary'}
              style={primaryColour ? { color: primaryColour } : undefined}
            /> Live tracker
          </CardTitle>
          <CardDescription>
            {isLoading ? 'Loading positions…' : `Showing ${filtered.length} of ${positions.length} tracked asset(s).`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Map sizing — on mobile the surrounding chrome (app header 56 +
              p-4 top 16 + page title 60 + filter card 110 + two 12-gaps +
              fixed bottom-nav reserve 80) is ~340px, so reserve that. Floor
              kept low so very short viewports don't push the map past the
              bottom nav. Desktop keeps the original calc with a higher
              minimum so the map never collapses on short windows. */}
          <div className="relative w-full h-[calc(100dvh-340px)] min-h-[200px] md:h-[calc(100vh-320px)] md:min-h-[480px]">
            <Map
              ref={mapRef}
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={initialView}
              mapStyle={mapStyle}
              style={{ width: '100%', height: '100%' }}
              // Disable fog/atmosphere — Mapbox styles default it on, which
              // sometimes throws `Cannot read properties of undefined
              // (reading 'get')` in `_evaluateOpacity` during HMR / unmount
              // when the transform has already been torn down. We don't use
              // the atmosphere visually, so killing it removes the crash
              // surface entirely. The `fog={null}` prop is supposed to do
              // this declaratively, but some styles re-apply fog after the
              // initial load — so we ALSO call `setFog(null)` imperatively
              // on every style-load event below for belt-and-braces.
              fog={null}
              // Force the cheap 2-D projection. Globe mode is what triggers
              // `_queryFogOpacity` per-frame; Mercator skips that path.
              projection="mercator"
              onLoad={(e: any) => {
                const m = e?.target;
                try {
                  m?.setFog?.(null);
                  m?.setTerrain?.(null);
                } catch { /* defensive — older mapbox-gl versions may not have these */ }
              }}
              onStyleData={(e: any) => {
                // Fires whenever a style (or piece of one) loads — including
                // user switching between Light / Streets / Satellite. The
                // new style can ship its own `fog` block, so re-null it.
                const m = e?.target;
                try {
                  m?.setFog?.(null);
                  m?.setTerrain?.(null);
                } catch { /* same */ }
              }}
            >
              <NavigationControl position="top-right" />
              {filtered.map((p) => {
                const isSelected = selected?.id === p.id;
                // Selected-marker ring uses the configured primary colour
                // when set; otherwise falls back to the design-system one.
                const ringClass = isSelected
                  ? (primaryColour ? 'ring-2' : 'ring-2 ring-primary')
                  : '';
                const ringStyle = isSelected && primaryColour
                  ? { boxShadow: `0 0 0 2px ${primaryColour}` }
                  : undefined;
                return (
                  <Marker
                    key={p.id}
                    longitude={p.lon}
                    latitude={p.lat}
                    anchor="bottom"
                    onClick={(e: any) => { e.originalEvent?.stopPropagation?.(); setSelected(p); }}
                  >
                    <div
                      className={`flex items-center justify-center w-9 h-9 md:w-7 md:h-7 rounded-full border-2 cursor-pointer shadow-md transition ${
                        p.online
                          ? 'bg-green-500 border-white text-white'
                          : 'bg-muted border-white text-muted-foreground'
                      } ${ringClass}`}
                      style={ringStyle}
                      title={p.fleetNumber ?? p.id}
                    >
                      <MapPin className="h-4 w-4 md:h-3.5 md:w-3.5" />
                    </div>
                  </Marker>
                );
              })}
            </Map>
            {selected && (
              <AssetDetailPanel
                assetId={selected.id}
                onClose={() => setSelected(null)}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
